import {
  AuthenticationTypeEnum,
  UserValidationStatusEnum,
} from 'src/api/ProactApiClient';
import type {PillProps} from 'src/design/atoms/Pill';

export type LoginStatusTone = 'ok' | 'info' | 'warning' | 'danger';

export interface LoginStatusView {
  pill: PillProps;
  summary: string;
  nextStep?: string;
  tone: LoginStatusTone;
}

export interface LoginStatusInput {
  status: UserValidationStatusEnum;
  authType: AuthenticationTypeEnum;
  providerName?: string;
  mfaEnabled: boolean;
  canResync?: boolean;
  managedByName?: string;
}

export const signInMethodLabel = (authType: AuthenticationTypeEnum): string =>
  authType === AuthenticationTypeEnum.SSO
    ? 'Single sign-on'
    : 'Email & password';

const READY: PillProps = {text: 'Ready', color: 'green'};
const UNKNOWN: LoginStatusView = {
  pill: {text: 'Unknown', color: 'gray'},
  summary: 'The sign-in status could not be determined.',
  nextStep: 'Refresh to check again.',
  tone: 'warning',
};

const describeSsoStatus = (
  status: UserValidationStatusEnum,
  provider: string
): LoginStatusView => {
  switch (status) {
    case UserValidationStatusEnum.Valid:
    case UserValidationStatusEnum.HasActiveMagicLink:
      return {
        pill: READY,
        summary: `Signs in through ${provider}. Passwords and multi-factor authentication are managed there.`,
        nextStep:
          'Enter the login email above on the Proact EHS login page to continue to the provider.',
        tone: 'ok',
      };
    case UserValidationStatusEnum.DoesNotExist:
      return {
        pill: {text: 'No login found', color: 'yellow'},
        summary: `An SSO login is normally created on the first sign-in through ${provider}.`,
        nextStep:
          'Ask the user to enter the login email above on the Proact EHS login page. If they have signed in before, contact Proact support to check their account.',
        tone: 'info',
      };
    default:
      return {
        ...UNKNOWN,
        nextStep: `Refresh to check again. Ask the company IT administrator about ${provider} credentials, or Proact support about account access and SSO configuration.`,
      };
  }
};

const describePasswordStatus = (
  status: UserValidationStatusEnum,
  mfaEnabled: boolean,
  resyncStep: string
): LoginStatusView => {
  switch (status) {
    case UserValidationStatusEnum.Valid:
      return {
        pill: READY,
        summary: mfaEnabled
          ? 'Signs in with email, password, and a code from an authenticator app.'
          : 'Signs in with email and password, or with an emailed sign-in link.',
        nextStep:
          'Forgotten password: the user picks "Forgot Password" on the login page.',
        tone: 'ok',
      };
    case UserValidationStatusEnum.HasActiveMagicLink:
      if (mfaEnabled) {
        return {
          pill: READY,
          summary:
            'Signs in with email, password, and a code from an authenticator app.',
          nextStep:
            'An earlier sign-in link is still recorded, but passwordless links cannot be used while multi-factor authentication is on.',
          tone: 'info',
        };
      }
      return {
        pill: {text: 'Link sent', color: 'blue'},
        summary:
          'An unused sign-in link is recorded. Links expire 60 minutes after they are created.',
        nextStep:
          'Ask the user to open the link, or to sign in with their password.',
        tone: 'info',
      };
    case UserValidationStatusEnum.DoesNotExist:
      return {
        pill: {text: 'No login', color: 'red'},
        summary: 'No login exists in the authentication system.',
        nextStep: resyncStep,
        tone: 'danger',
      };
    case UserValidationStatusEnum.OAuthUnconfirmed:
    case UserValidationStatusEnum.OAuthForcedChangePassword:
      return {
        pill: {text: 'Invite pending', color: 'yellow'},
        summary: 'Account setup is not complete.',
        nextStep: `Ask the user to open the link in their invitation email. If it is missing or expired: ${resyncStep}`,
        tone: 'warning',
      };
    case UserValidationStatusEnum.OAuthResetRequired:
      return {
        pill: {text: 'Reset required', color: 'red'},
        summary: 'The authentication system requires a new password.',
        nextStep: `Ask the user to use "Forgot Password" on the login page. If that fails: ${resyncStep}`,
        tone: 'danger',
      };
    case UserValidationStatusEnum.OAuthArchived:
    case UserValidationStatusEnum.OAuthCompromised:
      return {
        pill: {text: 'Blocked', color: 'red'},
        summary: 'An automated security check blocked this login.',
        nextStep: `Confirm the user's identity before recovery. ${resyncStep}`,
        tone: 'danger',
      };
    default:
      return {
        ...UNKNOWN,
        nextStep:
          'Refresh to check again. If the status remains unknown, contact Proact support before changing the login.',
      };
  }
};

export const describeLoginStatus = ({
  status,
  authType,
  providerName,
  mfaEnabled,
  canResync = true,
  managedByName,
}: LoginStatusInput): LoginStatusView => {
  if (status === UserValidationStatusEnum.Inactive) {
    return {
      pill: {text: 'No access', color: 'gray'},
      summary: 'No active user is attached to this login.',
      nextStep: 'Activate the user to restore access.',
      tone: 'warning',
    };
  }
  return authType === AuthenticationTypeEnum.SSO
    ? describeSsoStatus(status, providerName || 'the identity provider')
    : describePasswordStatus(
        status,
        mfaEnabled,
        canResync
          ? 'Resync the login to create it and send a new invitation.'
          : managedByName
          ? `Contact an administrator at ${managedByName} to recover the shared login.`
          : 'Activate access to this company before resyncing the login.'
      );
};

// These responses can be returned before Cognito's MFA settings are read.
export const hasVerifiedMfaStatus = (
  status: UserValidationStatusEnum
): boolean =>
  status !== UserValidationStatusEnum.Inactive &&
  status !== UserValidationStatusEnum.DoesNotExist &&
  status !== UserValidationStatusEnum.OAuthUnknown &&
  UserValidationStatusEnum[status] !== undefined;
